import { defineComponent as _defineComponent } from 'vue'
import videojs from "@mux/videojs-kit";
import { computed, inject, onBeforeUnmount, onMounted, ref, useAsync, useStore } from "@nuxtjs/composition-api";
import type VideoJsPlayer from "video.js/dist/types/player";

import { useAmplitude, useAuth, useConfig } from "@/composables";
import { getLtksStore } from "@/store/ltks";
import { getMediaObjectsStore } from "@/store/media-objects";
import { getProfilesStore } from "@/store/profiles";
import { videoDebugKey } from "@/utils/injections";
import logger, { logLevel } from "@/utils/logger";
import { Actions } from "@/utils/snowplow/enums";
import { createPostImpression } from "@/utils/snowplow/factory";

import "@mux/videojs-kit/dist/index.css";


export default /*#__PURE__*/_defineComponent({
  __name: 'LtkVideoPlayer',
  props: {
  ltkId: { type: String, required: true },
  autoplayDelay: { type: Number, required: false, default: 150 },
  isRunway: { type: Boolean, require: false, default: false },
},
  setup(__props) {

const props = __props;



const auth = useAuth();
const store = useStore();
const amplitude = useAmplitude();
const config = useConfig();
const player = ref<VideoJsPlayer | undefined>();
const videoPlayer = ref();
const isPlayerMuted = ref(true);
const numberOfLoops = ref(0);
const previousPlayerVolume = ref(0);
const ltk = computed(() => getLtksStore(store).ltks[props.ltkId]);
const profileName = computed(() => getProfilesStore(store).getProfileById(ltk.value?.profileId)?.displayName);
const videoMediaId = computed(() => ltk.value?.videoMediaId);
const mediaObject = computed(() => getMediaObjectsStore(store).getMediaObjectById(videoMediaId.value));
const videoMediaHeight = computed(() => mediaObject.value?.typeProperties.height);
const videoMediaWidth = computed(() => mediaObject.value?.typeProperties.width);
const isMuxStream = computed(() => mediaObject.value?.mediaCdnUrl.includes("mux"));
const hasVideoDebug = inject(videoDebugKey);
const playerOptions = computed(() => ({
  plugins: isMuxStream.value
    ? {
        mux: {
          debug: hasVideoDebug,
          data: {
            env_key: config.mux.envKey,
            player_name: config.mux.playerName,
            player_version: videojs.VERSION,
            sub_property_id: props.ltkId,
            video_id: mediaObject.value?.id,
            video_producer: ltk.value?.profileId,
            viewer_id: auth?.user?.id || amplitude?.client?.getUserId(),
            video_title: (profileName.value || ltk.value?.profileId) + "_" + props.ltkId,
          },
        },
      }
    : {},
  aspectRatio: props.isRunway ? "4:5" : `${videoMediaWidth.value}:${videoMediaHeight.value}`,
  language: "en",
  poster: ltk.value?.heroImage ?? "",
  preload: "auto",
  autoplay: !props.isRunway ? "muted" : false,
  playsinline: !props.isRunway,
  loop: true,
  controls: true,
  muted: !props.isRunway,
  sources: [
    {
      type: "application/x-mpegURL",
      src: mediaObject.value?.mediaCdnUrl ?? "",
    },
  ],
}));

const postImpressionPayload = computed(() =>
  createPostImpression(Actions.View, {
    id: props.ltkId ?? "",
    creator_id: ltk.value?.profileId ?? "",
    product_ids: ltk.value?.productIds,
  }),
);

if (!ltk.value) {
  useAsync(() => getLtksStore(store).fetchLtkById({ id: props.ltkId }));
}

onMounted(() => {
  try {
    player.value = videojs(videoPlayer.value as Element, playerOptions.value);

    if (hasVideoDebug) {
      player.value.httpSourceSelector();
    }

    // https://html.spec.whatwg.org/multipage/media.html#mediaevents
    player.value?.on("play", onPlayerPlay);
    player.value?.on("pause", onPlayerPause);
    player.value?.on("error", onPlayerError);
    player.value?.on("waiting", onPlayerWaiting);
    player.value?.on("volumechange", onPlayerVolumeChange);

    if (!props.isRunway) {
      setTimeout(() => {
        // https://videojs.com/blog/autoplay-best-practices-with-video-js/
        // Delay added to deal with mobile chrome not loading video.
        // This programmatic autoplay is just a fallback.
        player.value?.play();
      }, props.autoplayDelay);
    }
  } catch (error) {
    logger(logLevel.ERROR, "Error loading Video Player", {}, error);
  }
});

onBeforeUnmount(() => {
  player.value?.dispose();
});

const areCaptionsEnabled = () => {
  if (!videoPlayer.value) {
    return false;
  }
  return (videoPlayer.value as HTMLMediaElement).textTracks.length > 0;
};

const areCaptionsToggled = () => {
  if (!videoPlayer.value) {
    return false;
  }
  const tt = (videoPlayer.value as HTMLMediaElement).textTracks;
  for (let i = 0; i < tt.length; i++) {
    if (tt[i].kind === "captions" && tt[i].mode === "showing") {
      return true;
    }
  }
  return false;
};

const onPlayerPlay = () => {
  amplitude?.sendEvent("/video/start", {
    "ltk-id": props.ltkId,
    "profile-name": profileName.value,
    "number-of-loops": numberOfLoops.value,
    "captions-enabled": areCaptionsEnabled(),
    "captions-toggle": areCaptionsToggled(),
  });
};

const onPlayerPause = () => {
  amplitude?.sendEvent("/video/stop", {
    "ltk-id": props.ltkId,
    "profile-name": profileName.value,
    "number-of-loops": numberOfLoops.value,
  });
};

const onPlayerError = () => {
  amplitude?.sendEvent("/video/error", {
    "ltk-id": props.ltkId,
    "profile-name": profileName.value,
    "number-of-loops": numberOfLoops.value,
  });
};

const onPlayerWaiting = () => {
  numberOfLoops.value++;
};

const onPlayerVolumeChange = () => {
  const currentVolume = player.value?.volume() ?? 0;

  if (previousPlayerVolume.value === 0 && currentVolume > 0) {
    amplitude?.sendEvent("/video/unmute", {
      "ltk-id": props.ltkId,
      "profile-name": profileName.value,
      "number-of-loops": numberOfLoops.value,
    });
    isPlayerMuted.value = false;
  }

  if (previousPlayerVolume.value > 0 && currentVolume === 0) {
    amplitude?.sendEvent("/video/mute", {
      "ltk-id": props.ltkId,
      "profile-name": profileName.value,
      "number-of-loops": numberOfLoops.value,
    });
    isPlayerMuted.value = true;
  }

  // Player has a bug and reports mute/unmute toggling as the same volume
  if (previousPlayerVolume.value === currentVolume) {
    amplitude?.sendEvent(isPlayerMuted.value ? "/video/unmute" : "/video/mute", {
      "ltk-id": props.ltkId,
      "profile-name": profileName.value,
      "number-of-loops": numberOfLoops.value,
    });
    isPlayerMuted.value = !isPlayerMuted.value;
  }

  previousPlayerVolume.value = currentVolume;
};

return { __sfc: true,props, auth, store, amplitude, config, player, videoPlayer, isPlayerMuted, numberOfLoops, previousPlayerVolume, ltk, profileName, videoMediaId, mediaObject, videoMediaHeight, videoMediaWidth, isMuxStream, hasVideoDebug, playerOptions, postImpressionPayload, areCaptionsEnabled, areCaptionsToggled, onPlayerPlay, onPlayerPause, onPlayerError, onPlayerWaiting, onPlayerVolumeChange }
}

})